'use client';

import { QRCodeCanvas } from 'qrcode.react';

import { TradingLeaderboardMyRankingItem } from '@flipster/apis/v2/trading-leaderboards/types';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import { formatDatetime } from '@flipster/utils/datetime';
import { getOrdinalNumber } from '@flipster/utils/num';

import { FlipsterLogo } from '../../../shared/logo';
import { tierInfos } from '../../shared/constants';
import { LeaderboardTierIcon } from '../../shared/tier-icon';
import LeaderboardShareImage from './img_leaderboard_share.svg';

interface RankShareCardProps {
  copyUrl: string;
  referralCode?: string;
  ranking: TradingLeaderboardMyRankingItem;
}

export const RankShareCard = ({ copyUrl, referralCode, ranking }: RankShareCardProps) => {
  const { lastUpdatedAt, rank, lastRank, pnl, periodString, tier } = ranking;
  const title = (() => {
    if (!rank || rank > lastRank) return `I almost made the\nTop ${lastRank}`;
    return `I ranked ${rank}${getOrdinalNumber(rank).suffix}`;
  })();
  return (
    <>
      <div
        className={css({
          pos: 'relative',
          h: 396,
          p: 24,
          overflow: 'hidden',
          backgroundColor: 'background.default.level-0',
        })}
      >
        <div className={hstack({ justify: 'space-between', mb: 24 })}>
          <FlipsterLogo withType css={{ w: 83 }} />
          <p className={css({ textStyle: 'Text/S/Strong', color: 'content.default.level-3' })}>{periodString}</p>
        </div>
        <div className={stack({ gap: 12, position: 'relative', zIndex: 1 })}>
          <div className={stack({ textStyle: 'Display/L', gap: 4 })}>
            <p className={css({ color: 'content.default.level-5', whiteSpace: 'pre-line' })}>{title}</p>
            <Formatted.Number value={pnl} withColor withPlus precision={UsdtPrecision} />
          </div>
          <div className={stack({ textStyle: 'Text/S/Regular', gap: 2 })}>
            {tier != 'ALL' && (
              <div className={hstack({ gap: 4 })}>
                <LeaderboardTierIcon size={17} tier={tier} />
                <p className={css({ color: 'content.default.level-5' })}>
                  <FormattedMessage
                    id='Autogen_common.r251126.s_tierInfos_tier_label_league'
                    values={{
                      tierInfos_tier_label: tierInfos[tier].label,
                    }}
                  />
                </p>
              </div>
            )}
            <p className={css({ color: 'content.default.level-1' })}>
              <FormattedMessage
                id='Autogen_common.r251126.s_As_of_formatDatetime'
                values={{
                  formatDatetime: formatDatetime(lastUpdatedAt, { format: 'MMM dd, yyyy, HH:mm zz', isUTC: true }),
                }}
              />
            </p>
          </div>
        </div>
        <div
          className={css({
            pos: 'absolute',
            right: 0,
            bottom: -8,
          })}
        >
          <LeaderboardShareImage />
        </div>
      </div>
      <div
        className={flex({
          justifyContent: 'space-between',
          p: 16,
          color: 'inverse.black',
          backgroundColor: 'white',
        })}
      >
        <div className={vstack({ gap: 4, alignItems: 'flex-start' })}>
          <p
            className={css({
              textStyle: 'Text/S/Regular',
            })}
          >
            <FormattedMessage id='Referral.r260120.Share_card_notice' />
          </p>
          <h2
            className={css({
              textStyle: 'Title/XS',
            })}
          >
            <FormattedMessage
              id='Autogen_common.r251126.s_Referral_code_referralCode'
              values={{
                referralCode: referralCode,
              }}
            />
          </h2>
        </div>
        <QRCodeCanvas value={copyUrl} size={42} />
      </div>
    </>
  );
};
